import { StateCreator } from "zustand";
import { GlobalState } from "../globalStore";
import {
  type Situation,
  type SelectedSituation,
  type SelectedThought,
  type SelectedThoughtFree,
  type MindstringPrototype,
  type ThoughtGrouping,
  type Target,
  type ManageVersionMode,
  type SupportedLanguage,
} from "@/generated/fastapi";
import type {
  OriginDetailInput,
  OriginEditInput,
  OriginExtendInput,
} from "@/features/origins";
import type {
  BatchSituationConfigInput,
  SuggestedThoughtsReviewInput,
} from "@/features/studio";
import {
  ActiveBacklogViewType,
  ActiveStudioContentType,
  ActiveStudioMaintenanceMode,
  QuestionnaireViewMode,
  SelectedOrigin,
  SelectedSuggestedThought,
} from "@/shared/types";
import appSettings from "@/shared/config/appSettings";
import { mindstringPrototypeCodes } from "@/shared/config/backend";
import {
  sanitizeSelectedProfile,
  type SelectedProfileShape,
} from "@/shared/utils/selectedProfile";

// ==================== INTERIM STATE TYPE SYSTEM ====================
// Type-safe discriminated union approach for interim state management
// Each state ID maps to its specific payload type, providing compile-time safety

// Base state types
type DrawerStateBase = {
  isOpen: boolean;
};

type EntityEditState<T> = DrawerStateBase & {
  passedObject: T;
};

type ContextualDrawerState = DrawerStateBase & {
  value: string; // i.e. situationId or targetId or other string identifier
  prototypeCategory: MindstringPrototype;
};

type SingleTypeState<T> = DrawerStateBase & {
  value: T; // optional initial text for chat input or what ever type T is needed
};

type ArrayTypeState<T> = DrawerStateBase & {
  values: T[]; // array of items of type T
};

// Minimal type for thought reassignment (compatible with both MeiliThought and menu selections)
export type ThoughtReassignData = {
  objectID: string;
  situation_id?: string | null;
  prototype: MindstringPrototype;
};

type ThoughtReassignState = ArrayTypeState<ThoughtReassignData> & {
  source?: "needs_anchor_review";
};

type ThoughtSelectionInput = {
  prototypeCategory: MindstringPrototype;
  thoughtId: string;
  situationId?: string | null;
};

type ThoughtSelectionOrderInput = {
  prototypeCategory: MindstringPrototype;
  orderedThoughtIds: string[];
  situationId?: string | null;
};

type ApplySelectedProfileSelectionInput = {
  selectedProfile: SelectedProfileShape;
  existingSituationIds?: string[];
};

export type ProfileVersionDrawerMode =
  | ManageVersionMode
  | "extend_selection";

export type ActiveProfileVersion = {
  id: string;
  name: string;
  target_id?: string | null;
  language?: SupportedLanguage | null;
  latest_at?: string | null;
};

type ProfileShapingGuidance =
  | "manual_add"
  | "review_suggestions"
  | "loaded_selection_search";

// Comprehensive map of all interim state IDs to their exact payload types
type InterimStateMap = {
  // ===== Situation Management =====
  edit_situation: EntityEditState<Situation>;
  add_situation: DrawerStateBase;
  batch_situation_config: SingleTypeState<BatchSituationConfigInput>;
  suggested_thoughts_review: SingleTypeState<SuggestedThoughtsReviewInput>;

  // ===== Thought Management =====
  add_thoughts: ContextualDrawerState;
  extract_thoughts: ContextualDrawerState;
  reflect_thought: EntityEditState<{ objectID: string }>;
  trace_origin: EntityEditState<{ originId?: string | null }>;
  reassign_thought: ThoughtReassignState;
  reflect_thoughts: ContextualDrawerState;
  needs_anchor_review: DrawerStateBase;

  // ===== Target Management =====
  edit_target: EntityEditState<Target>;
  add_target: DrawerStateBase;

  // ===== Version Management =====
  manage_version: SingleTypeState<ProfileVersionDrawerMode>;

  // ===== Origin Management =====
  add_origin: DrawerStateBase;
  origin_detail: SingleTypeState<OriginDetailInput>;
  edit_origin: SingleTypeState<OriginEditInput>;
  extend_origin: SingleTypeState<OriginExtendInput>;

  // ===== Profile & Settings =====
  edit_personal: DrawerStateBase;
  profile_stats: DrawerStateBase;
  feedback_share: DrawerStateBase;
  parse_resume: DrawerStateBase;
  edit_output_settings: DrawerStateBase;
  search_filters: DrawerStateBase;

  // ===== Analysis Tools =====
  insight_bias: ContextualDrawerState;
  open_chat: SingleTypeState<string>;
  rerank_dialog: DrawerStateBase;

  // ===== Profile Shaping Guidance =====
  profile_shaping_guidance: SingleTypeState<ProfileShapingGuidance>;
};

// Union of all known static state IDs
type StaticInterimStateId = keyof InterimStateMap;

// Complete state container with partial support (states are optional until set)
type InterimStates = {
  [K in StaticInterimStateId]?: Partial<InterimStateMap[K]>;
};

// ==================== END INTERIM STATE TYPE SYSTEM ====================

type MeiliRefreshStampKey =
  // search instances depending on where they are used
  | "backlog_origins"
  | "studio_suggested_thoughts"
  | "studio_thoughts"
  // feature-local Meili-backed refresh flows
  | "extract_mindstrings";

type UiRefreshStampKey =
  // specific UI recompute cases
  "reset_reflections";

type PrototypeRefreshStamp = Record<MindstringPrototype, number>;
type MeiliRefreshStamp = {
  backlog_origins: number;
  studio_suggested_thoughts: PrototypeRefreshStamp;
  studio_thoughts: PrototypeRefreshStamp;
  extract_mindstrings: number;
};
type UiRefreshStamp = Record<UiRefreshStampKey, number>;
export type StudioSituationLoadState = Partial<
  Record<MindstringPrototype, Record<string, boolean>>
>;

const createPrototypeRefreshStamp = (): PrototypeRefreshStamp => ({
  [mindstringPrototypeCodes[0]]: 0,
  [mindstringPrototypeCodes[1]]: 0,
  [mindstringPrototypeCodes[2]]: 0,
  [mindstringPrototypeCodes[3]]: 0,
});

function appendThoughtIdToSelectedThoughtsFree(
  selectedThoughtsFree: SelectedThoughtFree[],
  prototypeCategory: MindstringPrototype,
  thoughtId: string,
): SelectedThoughtFree[] {
  const existingGroup = selectedThoughtsFree.find(
    (group) => group.prototype === prototypeCategory,
  );

  if (!existingGroup) {
    return [
      ...selectedThoughtsFree,
      {
        prototype: prototypeCategory,
        thoughts_ids: [thoughtId],
      },
    ];
  }

  if (existingGroup.thoughts_ids.includes(thoughtId)) {
    return selectedThoughtsFree;
  }

  return selectedThoughtsFree.map((group) =>
    group.prototype === prototypeCategory
      ? {
          ...group,
          thoughts_ids: [...group.thoughts_ids, thoughtId],
        }
      : group,
  );
}

function normalizeStudioSituationLoadStateForSituations({
  loadState,
  situations,
}: {
  loadState: StudioSituationLoadState | undefined;
  situations: Situation[];
}): StudioSituationLoadState {
  const validSituationIdsByPrototype = new Map<MindstringPrototype, Set<string>>();

  for (const prototype of mindstringPrototypeCodes) {
    validSituationIdsByPrototype.set(prototype, new Set<string>());
  }

  for (const situation of situations) {
    for (const prototype of situation.prototypes) {
      validSituationIdsByPrototype.get(prototype)?.add(situation.id);
    }
  }

  const nextLoadState: StudioSituationLoadState = {};

  for (const prototype of mindstringPrototypeCodes) {
    const prototypeState = loadState?.[prototype];
    if (!prototypeState) {
      continue;
    }

    const validSituationIds = validSituationIdsByPrototype.get(prototype);
    const nextPrototypeState: Record<string, boolean> = {};

    for (const [situationId, isLoaded] of Object.entries(prototypeState)) {
      if (!validSituationIds?.has(situationId) || isLoaded !== false) {
        continue;
      }

      nextPrototypeState[situationId] = false;
    }

    if (Object.keys(nextPrototypeState).length > 0) {
      nextLoadState[prototype] = nextPrototypeState;
    }
  }

  return nextLoadState;
}

function isStudioSituationLoadStateEqual(
  left: StudioSituationLoadState | undefined,
  right: StudioSituationLoadState | undefined,
) {
  return JSON.stringify(left ?? {}) === JSON.stringify(right ?? {});
}

function removeThoughtIdsFromSelectedThoughtsFree(
  selectedThoughtsFree: SelectedThoughtFree[],
  thoughtIds: string[],
): SelectedThoughtFree[] {
  return selectedThoughtsFree
    .map((group) => ({
      ...group,
      thoughts_ids: group.thoughts_ids.filter((id) => !thoughtIds.includes(id)),
    }))
    .filter((group) => group.thoughts_ids.length > 0);
}

function buildSelectedThoughtsFreeFromSelectedThoughts(
  selectedThoughts: SelectedThought[],
): SelectedThoughtFree[] {
  const thoughtsByPrototype = new Map<MindstringPrototype, string[]>();

  selectedThoughts.forEach((selection) => {
    const existingThoughtIds =
      thoughtsByPrototype.get(selection.prototype) ?? [];
    const seenThoughtIds = new Set(existingThoughtIds);

    selection.thoughts_ids.forEach((thoughtId) => {
      if (seenThoughtIds.has(thoughtId)) {
        return;
      }

      existingThoughtIds.push(thoughtId);
      seenThoughtIds.add(thoughtId);
    });

    thoughtsByPrototype.set(selection.prototype, existingThoughtIds);
  });

  return Array.from(thoughtsByPrototype.entries()).map(
    ([prototype, thoughts_ids]) => ({
      prototype,
      thoughts_ids,
    }),
  );
}

function getUniqueSelectedThoughtIds({
  selectedThoughts,
  selectedThoughtsFree,
}: {
  selectedThoughts: SelectedThought[];
  selectedThoughtsFree: SelectedThoughtFree[];
}): string[] {
  const orderedThoughtIds = selectedThoughtsFree.flatMap(
    (group) => group.thoughts_ids,
  );
  const seenThoughtIds = new Set(orderedThoughtIds);

  selectedThoughts.forEach((group) => {
    group.thoughts_ids.forEach((thoughtId) => {
      if (seenThoughtIds.has(thoughtId)) {
        return;
      }

      orderedThoughtIds.push(thoughtId);
      seenThoughtIds.add(thoughtId);
    });
  });

  return orderedThoughtIds;
}

function reconcileSelectedThoughtAssignments({
  selectedThoughts,
  selectedThoughtsFree,
  items,
  nextSituationId,
}: {
  selectedThoughts: SelectedThought[];
  selectedThoughtsFree: SelectedThoughtFree[];
  items: ThoughtReassignData[];
  nextSituationId: string | null;
}): SelectedThought[] {
  const selectedThoughtIdSet = new Set(
    getUniqueSelectedThoughtIds({
      selectedThoughts,
      selectedThoughtsFree,
    }),
  );
  const thoughtIdsByPrototype = new Map<MindstringPrototype, string[]>();

  items.forEach((item) => {
    if (!selectedThoughtIdSet.has(item.objectID)) {
      return;
    }

    const existingThoughtIds = thoughtIdsByPrototype.get(item.prototype) ?? [];
    if (!existingThoughtIds.includes(item.objectID)) {
      existingThoughtIds.push(item.objectID);
    }
    thoughtIdsByPrototype.set(item.prototype, existingThoughtIds);
  });

  const movedThoughtIdSet = new Set(
    Array.from(thoughtIdsByPrototype.values()).flat(),
  );
  const reconciledSelections = selectedThoughts
    .map((selection) => ({
      ...selection,
      thoughts_ids: selection.thoughts_ids.filter(
        (thoughtId) => !movedThoughtIdSet.has(thoughtId),
      ),
    }))
    .filter((selection) => selection.thoughts_ids.length > 0);

  if (!nextSituationId) {
    return reconciledSelections;
  }

  thoughtIdsByPrototype.forEach((thoughtIds, prototype) => {
    if (thoughtIds.length === 0) {
      return;
    }

    const existingGroup = reconciledSelections.find(
      (selection) =>
        selection.prototype === prototype &&
        selection.situation_id === nextSituationId,
    );

    if (!existingGroup) {
      reconciledSelections.push({
        prototype,
        situation_id: nextSituationId,
        thoughts_ids: thoughtIds,
      });
      return;
    }

    existingGroup.thoughts_ids = [
      ...existingGroup.thoughts_ids,
      ...thoughtIds.filter(
        (thoughtId) => !existingGroup.thoughts_ids.includes(thoughtId),
      ),
    ];
  });

  return reconciledSelections;
}

type CanLockResult = {
  canLock: boolean;
  reason: string | null; // If cannot lock, provide reason for better UX feedback
};

export interface UserChoiceV2State {
  //////* Generic controllers - Type-Safe Interim State Management
  interimState: InterimStates;

  // Type-safe setter with overloads for known IDs
  setInterimState<K extends StaticInterimStateId>(
    id: K,
    stateToSet: Partial<InterimStateMap[K]>,
  ): void;

  resetInterimState: (id: StaticInterimStateId) => void;

  //////* Switches */
  // Studio Prototype Category
  activePrototypeCategory: MindstringPrototype;
  setActivePrototypeCategory: (category: MindstringPrototype) => void;
  // Studio Grouping
  activeStudioGrouping: ThoughtGrouping;
  setActiveStudioGrouping: (grouping: ThoughtGrouping) => void;
  // Studio Content
  activeStudioContent: ActiveStudioContentType;
  setActiveStudioContent: (content: ActiveStudioContentType) => void;
  // Studio Selection Status
  activeStudioExportLock: boolean;
  setActiveStudioExportLock: (status: boolean) => void;
  toggleActiveStudioExportLock: () => void;
  canLockExport: () => CanLockResult; // Derived state to determine if export can be locked based on current conditions
  // Studio Maintenance Mode
  activeStudioMaintenanceMode: ActiveStudioMaintenanceMode;
  setActiveStudioMaintenanceMode: (mode: ActiveStudioMaintenanceMode) => void;

  // Active saved profile version for extension flow
  activeProfileVersion: ActiveProfileVersion | null;
  setActiveProfileVersion: (version: ActiveProfileVersion) => void;
  clearActiveProfileVersion: () => void;
  syncActiveProfileVersion: (version: ActiveProfileVersion | null) => void;

  // Backlog View(=Focus old naming)
  activeBacklogView: ActiveBacklogViewType;
  setActiveBacklogView: (option: ActiveBacklogViewType) => void;

  //////* UI Preferences */
  // Sidebar collapse state for desktop navigation
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;
  thoughtArrangementManualOverrides: Record<string, boolean>;
  setThoughtArrangementManualOverride: (
    scopeKey: string,
    value: boolean,
  ) => void;
  resetThoughtArrangementManualOverrides: () => void;
  studioSituationLoadState: StudioSituationLoadState;
  setStudioSituationLoaded: (
    prototype: MindstringPrototype,
    situationId: string,
    isLoaded: boolean,
  ) => void;
  setStudioSituationsLoaded: (
    prototype: MindstringPrototype,
    situationIds: string[],
    isLoaded: boolean,
  ) => void;
  resetStudioSituationLoadState: (prototype?: MindstringPrototype) => void;
  normalizeStudioSituationLoadState: (situations: Situation[]) => void;
  isStudioSituationLoaded: (
    prototype: MindstringPrototype,
    situationId: string,
  ) => boolean;

  //////* Refresh stamps */
  meiliRefreshStamp: MeiliRefreshStamp;
  incrementMeiliRefreshStamp: (
    key: MeiliRefreshStampKey,
    prototype?: MindstringPrototype,
  ) => void;
  uiRefreshStamp: UiRefreshStamp;
  incrementUiRefreshStamp: (key: UiRefreshStampKey) => void;

  //////* Search Parameters  */
  // Separate search queries per view to avoid performance issues with shared state
  searchQuerySelect: string;
  setSearchQuerySelect: (query: string) => void;
  resetSearchQuerySelect: () => void;
  searchQueryCheck: string;
  setSearchQueryCheck: (query: string) => void;
  resetSearchQueryCheck: () => void;
  searchQueryBacklog: string;
  setSearchQueryBacklog: (query: string) => void;
  resetSearchQueryBacklog: () => void;

  semanticRatio: number;
  setSemanticRatio: (ratio: number) => void;
  resetSemanticRatio: () => void;
  targetsToRerank: Target[]; // Array of targets (effectivelly [0] item) used to rerank search results
  setTargetsToRerank: (targets: Target[]) => void;
  resetTargetsToRerank: () => void;

  // Search UI controls exposed globally so navbar controls can update them
  backlogHitsPerPage: number;
  setBacklogHitsPerPage: (v: number) => void;
  backlogSortBy: string;
  setBacklogSortBy: (v: string) => void;
  backlogSemanticRatio: number;
  setBacklogSemanticRatio: (v: number) => void;
  backlogFilters: { language: string[] };
  setBacklogFilters: (filters: { language?: string[] }) => void;
  resetBacklogSearchAdjustments: () => void;

  selectHitsPerPage: number;
  setSelectHitsPerPage: (v: number) => void;
  selectSortBy: string;
  setSelectSortBy: (v: string) => void;
  selectSemanticRatio: number;
  setSelectSemanticRatio: (v: number) => void;
  selectFilters: { language: string[] };
  setSelectFilters: (filters: { language?: string[] }) => void;
  resetSelectSearchAdjustments: () => void;

  checkHitsPerPage: number;
  setCheckHitsPerPage: (v: number) => void;
  checkSortBy: string;
  setCheckSortBy: (v: string) => void;
  checkSemanticRatio: number;
  setCheckSemanticRatio: (v: number) => void;
  checkFilters: { language: string[] };
  setCheckFilters: (filters: { language?: string[] }) => void;
  resetCheckSearchAdjustments: () => void;

  //////* Questionnaire Parameters  */
  questionnaireViewModes: QuestionnaireViewMode[]; // multiple selection compatible with GenericSelector!
  setQuestionnaireViewModes: (modes: QuestionnaireViewMode[]) => void;

  //////* Selected Situations  */
  selectedSituations: SelectedSituation[];
  selectedSituationsSnapshot: SelectedSituation[]; // for comparison purposes
  addSelectedSituation: (
    prototypeCategory: MindstringPrototype,
    situationId: string,
  ) => void;
  getSelectedSituationIds: () => string[];
  isSelectedSituation: (
    prototypeCategory: MindstringPrototype,
    situationId: string,
  ) => boolean;
  removeSelectedSituation: (
    prototypeCategory: MindstringPrototype,
    situationId: string,
  ) => void;
  removeSelectedSituations: (situationIds: string[]) => void; // same as above but for multiple situations
  toggleSelectedSituation: (
    prototypeCategory: MindstringPrototype,
    situationId: string,
  ) => void;
  countSelectedSituations: () => number;
  saveSelectedSituationsSnapshot: () => void; // to save current selection as snapshot
  hasSelectedSituationsChanged: () => boolean; // to compare current selection with snapshot
  resetSelectedSituations: () => void;

  //////* Selected Thoughts  */
  selectedThoughts: SelectedThought[];
  selectedThoughtsSnapshot: SelectedThought[]; // for comparison purposes
  selectedThoughtsFreeSnapshot: SelectedThoughtFree[]; // for comparison purposes
  selectThought: (input: ThoughtSelectionInput) => void;
  getSelectedThoughtsByPrototype: (
    prototypeCategory: MindstringPrototype,
  ) => SelectedThought[];
  getSelectedThoughtsBySituationId: (situationId: string) => SelectedThought[];
  getSelectedThoughts(
    prototypeCategory?: MindstringPrototype,
    situationId?: string,
  ): SelectedThought[];
  getSelectedThoughtById: (
    prototypeCategory: MindstringPrototype,
    situationId: string,
    thoughtId: string,
  ) => SelectedThought | undefined;
  getSelectedThoughtIds: () => string[];
  isSelectedThought: (
    // prototypeCategory: MindstringPrototype,
    // situationId: string,
    thoughtId: string,
  ) => boolean;
  toggleThoughtSelection: (input: ThoughtSelectionInput) => void;
  toggleAllSelectedThoughts: (
    prototypeCategory: MindstringPrototype,
    situationId: string,
    allThoughtIds: string[],
  ) => void; // NEW: toggle all thoughts in a situation
  removeSelectedThoughts: (
    prototypeCategory: MindstringPrototype,
    situationId: string,
  ) => void;
  unselectThought: (thoughtId: string) => void;
  unselectThoughts: (thoughtIds: string[]) => void;
  reassignSelectedThoughts: (
    items: ThoughtReassignData[],
    nextSituationId: string | null,
  ) => void;
  reorderSituationThoughtSelection: (input: ThoughtSelectionOrderInput) => void;
  countSelectedThoughts: () => number;
  saveSelectedProfileSelectionSnapshot: () => void;
  hasSelectedProfileSelectionChanged: () => boolean;
  resetSelectedThoughts: () => void;

  //////* Selected Thoughts Free */
  selectedThoughtsFree: SelectedThoughtFree[];
  getSelectedThoughtsFree: (
    prototypeCategory?: MindstringPrototype,
  ) => SelectedThoughtFree[];
  reorderFreeThoughtSelection: (
    input: Omit<ThoughtSelectionOrderInput, "situationId">,
  ) => void;

  //////* Profile selection status (for convenience) */
  resetSelectedProfile: () => void;
  isSelectedProfile: () => boolean;
  applySelectedProfileSelection: (
    input: ApplySelectedProfileSelectionInput,
  ) => void;

  //////* Selected Suggested Thoughts  */
  selectedSuggestedThoughts: SelectedSuggestedThought;
  addSelectedSuggestedThought: (thoughtId: string) => void;
  addSelectedSuggestedThoughts: (thoughtIds: string[]) => void; // NEW: add multiple suggested thoughts
  toggleSelectedSuggestedThought: (thoughtId: string) => void;
  toggleAllSelectedSuggestedThoughts: (allThoughtIds: string[]) => void; // NEW: toggle all suggested thoughts
  isSelectedSuggestedThought: (thoughtId: string) => boolean;
  removeSelectedSuggestedThoughts: (thoughtIds: string[]) => void;
  countSelectedSuggestedThoughts: () => number;
  resetSelectedSuggestedThoughts: () => void;

  ////// * Selected Origins  */
  // selectedOrigins: SelectedOrigin[];
  selectedOrigins: SelectedOrigin;
  addSelectedOrigin: (originId: string) => void;
  toggleSelectedOrigin: (originId: string) => void;
  isSelectedOrigin: (originId: string) => boolean;
  removeSelectedOrigins: (originIds: string[]) => void;
  countSelectedOrigins: () => number;
  getSelectedOriginIds: () => string[]; // NEW: Get all selected origin IDs
  resetSelectedOrigins: () => void;
}

type PersistableUserChoicesState = Pick<
  UserChoiceV2State,
  | "selectedSituations"
  | "selectedThoughts"
  | "selectedThoughtsFree"
  | "searchQueryCheck"
  | "searchQuerySelect"
  | "searchQueryBacklog"
  | "checkFilters"
  | "checkSemanticRatio"
  | "checkHitsPerPage"
  | "checkSortBy"
  | "selectFilters"
  | "selectSemanticRatio"
  | "selectHitsPerPage"
  | "selectSortBy"
  | "backlogFilters"
  | "backlogSemanticRatio"
  | "backlogHitsPerPage"
  | "backlogSortBy"
  | "targetsToRerank"
  | "activeBacklogView"
  | "activeStudioGrouping"
  | "activeStudioContent"
  | "activeStudioExportLock"
  | "activePrototypeCategory"
  | "activeProfileVersion"
  | "studioSituationLoadState"
>;

export const createUserChoicesV2Slice: StateCreator<
  GlobalState,
  [],
  [],
  UserChoiceV2State
> = (set, get) => ({
  //////* Generic controllers - Type-Safe Interim State Management
  interimState: {},

  // Overloaded implementation maintains backward compatibility while adding type safety
  setInterimState: (
    id: StaticInterimStateId,
    stateToSet: Partial<InterimStateMap[StaticInterimStateId]>,
  ) =>
    set((state) => ({
      interimState: {
        ...state.interimState,
        [id]: {
          ...state.interimState[id],
          ...stateToSet,
        },
      },
    })),

  resetInterimState: (id: StaticInterimStateId) =>
    set((state) => ({
      interimState: {
        ...state.interimState,
        [id]: {},
      },
    })),

  //////* Switches */
  // Studio Prototype Category
  activePrototypeCategory: appSettings.default
    .activePrototypeCategory as MindstringPrototype,
  setActivePrototypeCategory: (category) =>
    set({ activePrototypeCategory: category }),
  // Studio Grouping
  activeStudioGrouping: appSettings.default
    .activeStudioGrouping as ThoughtGrouping,
  setActiveStudioGrouping: (grouping) =>
    set({ activeStudioGrouping: grouping }),
  // Studio Content
  activeStudioContent: appSettings.default
    .activeStudioContent as ActiveStudioContentType,
  setActiveStudioContent: (content) =>
    set({
      activeStudioContent: content === "suggested" ? "confirmed" : content,
    }),
  // Studio Selection Status
  activeStudioExportLock: appSettings.default.activeStudioExportLock,
  setActiveStudioExportLock: (status) =>
    set({ activeStudioExportLock: status }),
  toggleActiveStudioExportLock: () =>
    set((state) => ({
      activeStudioExportLock: !state.activeStudioExportLock,
    })),
  canLockExport: () => ({ canLock: true, reason: null }),
  // Studio Maintenance Mode
  activeStudioMaintenanceMode: appSettings.default
    .activeStudioMaintenanceMode as ActiveStudioMaintenanceMode,
  setActiveStudioMaintenanceMode: (mode) =>
    set({ activeStudioMaintenanceMode: mode }),

  activeProfileVersion: null,
  setActiveProfileVersion: (version) =>
    set({ activeProfileVersion: version }),
  clearActiveProfileVersion: () => set({ activeProfileVersion: null }),
  syncActiveProfileVersion: (version) =>
    set({ activeProfileVersion: version }),

  // Backlog View(=Focus old naming)
  activeBacklogView: appSettings.default.activeBacklogView,
  setActiveBacklogView: (option) => set({ activeBacklogView: option }),

  //////* UI Preferences */
  isSidebarCollapsed: true, // Default to compact desktop rail
  setIsSidebarCollapsed: (collapsed) => set({ isSidebarCollapsed: collapsed }),
  toggleSidebar: () =>
    set((state) => ({ isSidebarCollapsed: !state.isSidebarCollapsed })),
  thoughtArrangementManualOverrides: {},
  setThoughtArrangementManualOverride: (scopeKey, value) =>
    set((state) => ({
      thoughtArrangementManualOverrides: {
        ...state.thoughtArrangementManualOverrides,
        [scopeKey]: value,
      },
    })),
  resetThoughtArrangementManualOverrides: () =>
    set({ thoughtArrangementManualOverrides: {} }),
  studioSituationLoadState: {},
  setStudioSituationLoaded: (prototype, situationId, isLoaded) =>
    set((state) => {
      const prototypeState = state.studioSituationLoadState[prototype] ?? {};
      const nextPrototypeState = { ...prototypeState };

      if (isLoaded) {
        delete nextPrototypeState[situationId];
      } else {
        nextPrototypeState[situationId] = false;
      }

      const nextLoadState = { ...state.studioSituationLoadState };
      if (Object.keys(nextPrototypeState).length > 0) {
        nextLoadState[prototype] = nextPrototypeState;
      } else {
        delete nextLoadState[prototype];
      }

      return { studioSituationLoadState: nextLoadState };
    }),
  setStudioSituationsLoaded: (prototype, situationIds, isLoaded) =>
    set((state) => {
      const prototypeState = state.studioSituationLoadState[prototype] ?? {};
      const nextPrototypeState = { ...prototypeState };

      for (const situationId of situationIds) {
        if (isLoaded) {
          delete nextPrototypeState[situationId];
        } else {
          nextPrototypeState[situationId] = false;
        }
      }

      const nextLoadState = { ...state.studioSituationLoadState };
      if (Object.keys(nextPrototypeState).length > 0) {
        nextLoadState[prototype] = nextPrototypeState;
      } else {
        delete nextLoadState[prototype];
      }

      return { studioSituationLoadState: nextLoadState };
    }),
  resetStudioSituationLoadState: (prototype) =>
    set((state) => {
      if (!prototype) {
        return { studioSituationLoadState: {} };
      }

      const nextLoadState = { ...state.studioSituationLoadState };
      delete nextLoadState[prototype];
      return { studioSituationLoadState: nextLoadState };
    }),
  normalizeStudioSituationLoadState: (situations) =>
    set((state) => {
      const nextLoadState = normalizeStudioSituationLoadStateForSituations({
        loadState: state.studioSituationLoadState,
        situations,
      });

      if (
        isStudioSituationLoadStateEqual(
          state.studioSituationLoadState,
          nextLoadState,
        )
      ) {
        return {};
      }

      return { studioSituationLoadState: nextLoadState };
    }),
  isStudioSituationLoaded: (prototype, situationId) =>
    get().studioSituationLoadState[prototype]?.[situationId] !== false,

  //////* Refresh stamps */
  meiliRefreshStamp: {
    // search instances depending on where they are used
    backlog_origins: 0,
    studio_suggested_thoughts: createPrototypeRefreshStamp(),
    studio_thoughts: createPrototypeRefreshStamp(),
    extract_mindstrings: 0,
  },

  incrementMeiliRefreshStamp: (
    key: MeiliRefreshStampKey,
    prototype?: MindstringPrototype,
  ) =>
    set((state) => {
      if (key === "studio_thoughts" || key === "studio_suggested_thoughts") {
        const currentStamp = state.meiliRefreshStamp[key];

        if (prototype) {
          return {
            meiliRefreshStamp: {
              ...state.meiliRefreshStamp,
              [key]: {
                ...currentStamp,
                [prototype]: currentStamp[prototype] + 1,
              },
            },
          };
        }

        const nextStamp = { ...currentStamp };
        for (const code of mindstringPrototypeCodes) {
          nextStamp[code] = currentStamp[code] + 1;
        }

        return {
          meiliRefreshStamp: {
            ...state.meiliRefreshStamp,
            [key]: nextStamp,
          },
        };
      }

      return {
        meiliRefreshStamp: {
          ...state.meiliRefreshStamp,
          [key]: state.meiliRefreshStamp[key] + 1,
        },
      };
    }),

  uiRefreshStamp: {
    reset_reflections: 0,
  },

  incrementUiRefreshStamp: (key: UiRefreshStampKey) =>
    set((state) => ({
      uiRefreshStamp: {
        ...state.uiRefreshStamp,
        [key]: (state.uiRefreshStamp[key] || 0) + 1,
      },
    })),

  //////* Search Parameters  */

  searchQuerySelect: "",
  setSearchQuerySelect: (query) => set({ searchQuerySelect: query }),
  resetSearchQuerySelect: () => set({ searchQuerySelect: "" }),
  searchQueryCheck: "",
  setSearchQueryCheck: (query) => set({ searchQueryCheck: query }),
  resetSearchQueryCheck: () => set({ searchQueryCheck: "" }),
  searchQueryBacklog: "",
  setSearchQueryBacklog: (query) => set({ searchQueryBacklog: query }),
  resetSearchQueryBacklog: () => set({ searchQueryBacklog: "" }),

  semanticRatio: 0.5,
  setSemanticRatio: (ratio) => set({ semanticRatio: ratio }),
  resetSemanticRatio: () => set({ semanticRatio: 0.5 }),
  targetsToRerank: [],
  setTargetsToRerank: (targets) => set({ targetsToRerank: targets }),
  resetTargetsToRerank: () => set({ targetsToRerank: [] }),

  // Search UI controls exposed globally so navbar controls can update them
  backlogHitsPerPage: appSettings.search?.HITS_PER_PAGE || 20,
  setBacklogHitsPerPage: (v: number) => set({ backlogHitsPerPage: v }),
  backlogSortBy: "origins:latest_at:desc",
  setBacklogSortBy: (v: string) => set({ backlogSortBy: v }),
  backlogSemanticRatio: 0.5,
  setBacklogSemanticRatio: (v: number) => set({ backlogSemanticRatio: v }),
  backlogFilters: { language: [] },
  setBacklogFilters: (filters: { language?: string[] }) =>
    set((state) => ({
      backlogFilters: { ...state.backlogFilters, ...filters },
    })),
  resetBacklogSearchAdjustments: () =>
    set({
      backlogHitsPerPage: appSettings.search?.HITS_PER_PAGE || 20,
      backlogSortBy: "origins:latest_at:desc",
      backlogSemanticRatio: 0.5,
      backlogFilters: { language: [] },
    }),

  selectHitsPerPage: appSettings.search?.HITS_PER_PAGE || 20,
  setSelectHitsPerPage: (v: number) => set({ selectHitsPerPage: v }),
  selectSortBy: "thoughts:latest_at:desc",
  setSelectSortBy: (v: string) => set({ selectSortBy: v }),
  selectSemanticRatio: 0.5,
  setSelectSemanticRatio: (v: number) => set({ selectSemanticRatio: v }),
  selectFilters: { language: [] },
  setSelectFilters: (filters: { language?: string[] }) =>
    set((state) => ({ selectFilters: { ...state.selectFilters, ...filters } })),
  resetSelectSearchAdjustments: () =>
    set({
      selectHitsPerPage: appSettings.search?.HITS_PER_PAGE || 20,
      selectSortBy: "thoughts:latest_at:desc",
      selectSemanticRatio: 0.5,
      selectFilters: { language: [] },
    }),

  checkHitsPerPage: appSettings.search?.HITS_PER_PAGE || 20,
  setCheckHitsPerPage: (v: number) => set({ checkHitsPerPage: v }),
  checkSortBy: "suggested_thoughts:latest_at:desc",
  setCheckSortBy: (v: string) => set({ checkSortBy: v }),
  checkSemanticRatio: 0.5,
  setCheckSemanticRatio: (v: number) => set({ checkSemanticRatio: v }),
  checkFilters: { language: [] },
  setCheckFilters: (filters: { language?: string[] }) =>
    set((state) => ({ checkFilters: { ...state.checkFilters, ...filters } })),
  resetCheckSearchAdjustments: () =>
    set({
      checkHitsPerPage: appSettings.search?.HITS_PER_PAGE || 20,
      checkSortBy: "suggested_thoughts:latest_at:desc",
      checkSemanticRatio: 0.5,
      checkFilters: { language: [] },
    }),

  //////* Questionnaire Parameters  */
  questionnaireViewModes: [appSettings.default.questionnaireViewMode],
  setQuestionnaireViewModes: (modes) => set({ questionnaireViewModes: modes }),

  //////* Selected Situations  */
  selectedSituations: [
    {
      prototype: "job_activity",
      situations_ids: [],
    },
    {
      prototype: "learning_outcome",
      situations_ids: [],
    },
    {
      prototype: "workplace_characteristic",
      situations_ids: [],
    },
    {
      prototype: "individual_description",
      situations_ids: [],
    },
  ],
  selectedSituationsSnapshot: [
    {
      prototype: "job_activity",
      situations_ids: [],
    },
    {
      prototype: "learning_outcome",
      situations_ids: [],
    },
    {
      prototype: "workplace_characteristic",
      situations_ids: [],
    },
    {
      prototype: "individual_description",
      situations_ids: [],
    },
  ],
  addSelectedSituation: (prototypeCategory, situationId) => {
    const exists = get().isSelectedSituation(prototypeCategory, situationId);
    if (exists) return; // do not add duplicates
    set((state) => {
      const existingGroup = state.selectedSituations.find(
        (group) => group.prototype === prototypeCategory,
      );

      if (existingGroup) {
        return {
          selectedSituations: state.selectedSituations.map((group) =>
            group.prototype === prototypeCategory
              ? {
                  ...group,
                  situations_ids: [...group.situations_ids, situationId],
                }
              : group,
          ),
        };
      } else {
        return {
          selectedSituations: [
            ...state.selectedSituations,
            {
              prototype: prototypeCategory,
              situations_ids: [situationId],
            },
          ],
        };
      }
    });
  },
  getSelectedSituationIds: () => {
    return get().selectedSituations.flatMap((group) => group.situations_ids);
  },
  isSelectedSituation: (prototypeCategory, situationId) => {
    const group = get().selectedSituations.find(
      (group) => group.prototype === prototypeCategory,
    );
    if (!group) {
      return false;
    }
    return group.situations_ids.includes(situationId);
  },

  removeSelectedSituation: (prototypeCategory, situationId) => {
    set((state) => ({
      selectedSituations: state.selectedSituations.map((group) =>
        group.prototype === prototypeCategory
          ? {
              ...group,
              situations_ids: group.situations_ids.filter(
                (id) => id !== situationId,
              ),
            }
          : group,
      ),
    }));
  },

  removeSelectedSituations: (situationIds: string[]) =>
    set((state) => ({
      selectedSituations: state.selectedSituations.map((group) => ({
        ...group,
        situations_ids: group.situations_ids.filter(
          (id) => !situationIds.includes(id),
        ),
      })),
    })),

  toggleSelectedSituation: (prototypeCategory, situationId) => {
    const isSelected = get().isSelectedSituation(
      prototypeCategory,
      situationId,
    );
    if (isSelected) {
      // get().removeSelectedSituations([situationId]);
      get().removeSelectedSituation(prototypeCategory, situationId);
    } else {
      get().addSelectedSituation(prototypeCategory, situationId);
    }
  },

  countSelectedSituations: () => {
    return get().selectedSituations.reduce(
      (total, group) => total + group.situations_ids.length,
      0,
    );
  },
  saveSelectedSituationsSnapshot: () =>
    set((state) => ({
      selectedSituationsSnapshot: JSON.parse(
        JSON.stringify(state.selectedSituations),
      ),
    })),
  hasSelectedSituationsChanged: () => {
    const current = get().selectedSituations;
    const snapshot = get().selectedSituationsSnapshot;

    return JSON.stringify(current) !== JSON.stringify(snapshot);
  },

  resetSelectedSituations: () =>
    set({
      selectedSituations: [
        {
          prototype: "job_activity",
          situations_ids: [],
        },
        {
          prototype: "learning_outcome",
          situations_ids: [],
        },
        {
          prototype: "workplace_characteristic",
          situations_ids: [],
        },
        {
          prototype: "individual_description",
          situations_ids: [],
        },
      ],
      selectedSituationsSnapshot: [
        {
          prototype: "job_activity",
          situations_ids: [],
        },
        {
          prototype: "learning_outcome",
          situations_ids: [],
        },
        {
          prototype: "workplace_characteristic",
          situations_ids: [],
        },
        {
          prototype: "individual_description",
          situations_ids: [],
        },
      ],
    }),

  //////* Selected Thoughts  */
  selectedThoughts: [], // it must be dynamically populated based on parameters
  selectedThoughtsSnapshot: [], // for comparison purposes
  selectedThoughtsFreeSnapshot: [], // for comparison purposes

  selectThought: ({ prototypeCategory, situationId, thoughtId }) => {
    if (get().isSelectedThought(thoughtId)) {
      return;
    }

    set((state) => {
      const nextSelectedThoughtsFree = appendThoughtIdToSelectedThoughtsFree(
        state.selectedThoughtsFree,
        prototypeCategory,
        thoughtId,
      );

      if (!situationId) {
        return {
          selectedThoughtsFree: nextSelectedThoughtsFree,
        };
      }

      const existingGroup = state.selectedThoughts.find(
        (group) =>
          group.prototype === prototypeCategory &&
          group.situation_id === situationId,
      );

      return {
        selectedThoughtsFree: nextSelectedThoughtsFree,
        selectedThoughts: existingGroup
          ? state.selectedThoughts.map((group) =>
              group.prototype === prototypeCategory &&
              group.situation_id === situationId
                ? {
                    ...group,
                    thoughts_ids: [...group.thoughts_ids, thoughtId],
                  }
                : group,
            )
          : [
              ...state.selectedThoughts,
              {
                prototype: prototypeCategory,
                situation_id: situationId,
                thoughts_ids: [thoughtId],
              },
            ],
      };
    });

    if (situationId) {
      get().addSelectedSituation(prototypeCategory, situationId);
    }
  },

  getSelectedThoughtsByPrototype: (prototypeCategory) =>
    get().selectedThoughts.filter(
      (item) => item.prototype === prototypeCategory,
    ),

  getSelectedThoughtsBySituationId: (situationId) =>
    get().selectedThoughts.filter((item) => item.situation_id === situationId),

  getSelectedThoughts(prototypeCategory?, situationId?): SelectedThought[] {
    let filteredThoughts = get().selectedThoughts;

    if (prototypeCategory) {
      filteredThoughts = filteredThoughts.filter(
        (item) => item.prototype === prototypeCategory,
      );
    }

    if (situationId) {
      filteredThoughts = filteredThoughts.filter(
        (item) => item.situation_id === situationId,
      );
    }

    return filteredThoughts;
  },
  getSelectedThoughtById: (prototypeCategory, situationId, thoughtId) =>
    get().selectedThoughts.find(
      (item) =>
        item.prototype === prototypeCategory &&
        item.situation_id === situationId &&
        item.thoughts_ids.includes(thoughtId),
    ),

  getSelectedThoughtIds: () => {
    return getUniqueSelectedThoughtIds({
      selectedThoughts: get().selectedThoughts,
      selectedThoughtsFree: get().selectedThoughtsFree,
    });
  },

  isSelectedThought: (
    //
    // prototypeCategory,
    // situationId,
    thoughtId,
  ) => {
    // const group = get().selectedThoughts.find(
    //   (item) =>
    //     item.prototype === prototypeCategory &&
    //     item.situation_id === situationId,
    // );
    return get().getSelectedThoughtIds().includes(thoughtId);
  },

  toggleThoughtSelection: ({ prototypeCategory, situationId, thoughtId }) => {
    if (get().isSelectedThought(thoughtId)) {
      get().unselectThought(thoughtId);
      return;
    }

    get().selectThought({ prototypeCategory, situationId, thoughtId });
  },

  toggleAllSelectedThoughts: (
    prototypeCategory,
    situationId,
    allThoughtIds,
  ) => {
    const selectedThoughts = get().getSelectedThoughts(
      prototypeCategory,
      situationId,
    );
    const selectedThoughtIds =
      selectedThoughts.length > 0 ? selectedThoughts[0].thoughts_ids : [];

    const areAllSelected = allThoughtIds.every((id) =>
      selectedThoughtIds.includes(id),
    );

    if (areAllSelected) {
      // Unselect all thoughts from selection
      get().unselectThoughts(allThoughtIds);
      // Unselect this situation as well
      get().removeSelectedSituation(prototypeCategory, situationId);
    } else {
      // Add all thoughts to selection
      allThoughtIds.forEach((thoughtId) => {
        if (!selectedThoughtIds.includes(thoughtId)) {
          get().selectThought({ prototypeCategory, situationId, thoughtId });
        }
      });
      // Ensure the situation is selected
      get().addSelectedSituation(prototypeCategory, situationId);
    }
  },

  removeSelectedThoughts: (prototypeCategory, situationId) =>
    set((state) => {
      const thoughtIdsToRemove = state.selectedThoughts
        .filter(
          (item) =>
            item.situation_id === situationId &&
            item.prototype === prototypeCategory,
        )
        .flatMap((item) => item.thoughts_ids);
      const nextSelectedThoughts = state.selectedThoughts.filter(
        (item) =>
          !(
            item.situation_id === situationId &&
            item.prototype === prototypeCategory
          ),
      );
      const nextSelectedThoughtsFree = removeThoughtIdsFromSelectedThoughtsFree(
        state.selectedThoughtsFree,
        thoughtIdsToRemove,
      );
      const hasSelectedThoughts =
        getUniqueSelectedThoughtIds({
          selectedThoughts: nextSelectedThoughts,
          selectedThoughtsFree: nextSelectedThoughtsFree,
        }).length > 0;

      return {
        activeStudioExportLock: hasSelectedThoughts
          ? state.activeStudioExportLock
          : false,
        selectedThoughts: nextSelectedThoughts,
        selectedThoughtsFree: nextSelectedThoughtsFree,
      };
    }),
  unselectThoughts: (thoughtIds) =>
    set((state) => {
      const nextSelectedThoughts = state.selectedThoughts
        .map((item) => ({
          ...item,
          thoughts_ids: item.thoughts_ids.filter(
            (id) => !thoughtIds.includes(id),
          ),
        }))
        .filter((item) => item.thoughts_ids.length > 0);

      const emptiedSelections = state.selectedThoughts.filter((selection) => {
        const nextThoughtIds = selection.thoughts_ids.filter(
          (id) => !thoughtIds.includes(id),
        );
        return (
          selection.situation_id &&
          selection.thoughts_ids.length > 0 &&
          nextThoughtIds.length === 0
        );
      });

      const nextSelectedSituations = state.selectedSituations.map((group) => {
        const emptiedSituationIds = emptiedSelections
          .filter((selection) => selection.prototype === group.prototype)
          .map((selection) => selection.situation_id)
          .filter(Boolean);

        if (emptiedSituationIds.length === 0) {
          return group;
        }

        return {
          ...group,
          situations_ids: group.situations_ids.filter(
            (id) => !emptiedSituationIds.includes(id),
          ),
        };
      });

      const nextSelectedThoughtsFree = removeThoughtIdsFromSelectedThoughtsFree(
        state.selectedThoughtsFree,
        thoughtIds,
      );
      const hasSelectedThoughts =
        getUniqueSelectedThoughtIds({
          selectedThoughts: nextSelectedThoughts,
          selectedThoughtsFree: nextSelectedThoughtsFree,
        }).length > 0;

      return {
        activeStudioExportLock: hasSelectedThoughts
          ? state.activeStudioExportLock
          : false,
        selectedSituations: nextSelectedSituations,
        selectedThoughts: nextSelectedThoughts,
        selectedThoughtsFree: nextSelectedThoughtsFree,
      };
    }),
  unselectThought: (thoughtId) => {
    get().unselectThoughts([thoughtId]);
  },
  reassignSelectedThoughts: (items, nextSituationId) => {
    set((state) => ({
      selectedThoughts: reconcileSelectedThoughtAssignments({
        selectedThoughts: state.selectedThoughts,
        selectedThoughtsFree: state.selectedThoughtsFree,
        items,
        nextSituationId,
      }),
    }));

    if (!nextSituationId) {
      return;
    }

    Array.from(new Set(items.map((item) => item.prototype))).forEach(
      (prototypeCategory) => {
        get().addSelectedSituation(prototypeCategory, nextSituationId);
      },
    );
  },
  reorderSituationThoughtSelection: ({
    prototypeCategory,
    situationId,
    orderedThoughtIds,
  }) =>
    set((state) => ({
      selectedThoughts: state.selectedThoughts.map((selection) =>
        selection.prototype === prototypeCategory &&
        selection.situation_id === (situationId ?? null)
          ? {
              ...selection,
              thoughts_ids: orderedThoughtIds,
            }
          : selection,
      ),
    })),

  countSelectedThoughts: () => {
    return get().getSelectedThoughtIds().length;
  },
  saveSelectedProfileSelectionSnapshot: () =>
    set((state) => ({
      selectedThoughtsSnapshot: JSON.parse(
        JSON.stringify(state.selectedThoughts),
      ),
      selectedThoughtsFreeSnapshot: JSON.parse(
        JSON.stringify(state.selectedThoughtsFree),
      ),
    })),
  hasSelectedProfileSelectionChanged: () => {
    const current = {
      selectedThoughts: get().selectedThoughts,
      selectedThoughtsFree: get().selectedThoughtsFree,
    };
    const snapshot = {
      selectedThoughts: get().selectedThoughtsSnapshot,
      selectedThoughtsFree: get().selectedThoughtsFreeSnapshot,
    };

    return JSON.stringify(current) !== JSON.stringify(snapshot);
  },

  resetSelectedThoughts: () =>
    set({
      activeStudioExportLock: false,
      selectedThoughts: [],
      selectedThoughtsFree: [],
    }),

  //////* Selected Thoughts Free */
  selectedThoughtsFree: [],
  getSelectedThoughtsFree(prototypeCategory?) {
    let filteredThoughtsFree = get().selectedThoughtsFree;

    if (prototypeCategory) {
      filteredThoughtsFree = filteredThoughtsFree.filter(
        (item) => item.prototype === prototypeCategory,
      );
    }

    return filteredThoughtsFree;
  },
  reorderFreeThoughtSelection: ({ prototypeCategory, orderedThoughtIds }) =>
    set((state) => ({
      selectedThoughtsFree: state.selectedThoughtsFree.map((selection) =>
        selection.prototype === prototypeCategory
          ? {
              ...selection,
              thoughts_ids: orderedThoughtIds,
            }
          : selection,
      ),
    })),
  //////* Profile selection status (for convenience) */
  resetSelectedProfile: () => {
    get().resetSelectedSituations();
    get().resetSelectedThoughts();
  },
  isSelectedProfile: () => {
    const selectedThoughtsCount = get().countSelectedThoughts();
    const selectedSituationsCount = get().countSelectedSituations();
    return selectedThoughtsCount > 0 || selectedSituationsCount > 0;
  },
  applySelectedProfileSelection: ({
    selectedProfile,
    existingSituationIds,
  }) => {
    const sanitizedSelectedProfile = sanitizeSelectedProfile({
      selectedProfile,
      existingSituationIds,
    });

    set({
      selectedSituations: sanitizedSelectedProfile.selected_situations ?? [],
      selectedThoughts: sanitizedSelectedProfile.selected_thoughts ?? [],
      selectedThoughtsFree:
        sanitizedSelectedProfile.selected_thoughts_free ?? [],
      selectedSuggestedThoughts:
        sanitizedSelectedProfile.selected_suggested_thoughts ?? [],
    });
  },

  //////* Selected Suggested Thoughts  */
  selectedSuggestedThoughts: [],

  addSelectedSuggestedThought: (thoughtId) => {
    const exists = get().isSelectedSuggestedThought(thoughtId);
    if (exists) return; // do not add duplicates

    set((state) => ({
      selectedSuggestedThoughts: [
        ...state.selectedSuggestedThoughts,
        thoughtId,
      ],
    }));
  },

  addSelectedSuggestedThoughts: (thoughtIds) => {
    set((state) => {
      const newThoughts = thoughtIds.filter(
        (id) => !state.selectedSuggestedThoughts.includes(id),
      );
      return {
        selectedSuggestedThoughts: [
          ...state.selectedSuggestedThoughts,
          ...newThoughts,
        ],
      };
    });
  },

  toggleSelectedSuggestedThought: (thoughtId) => {
    const exists = get().isSelectedSuggestedThought(thoughtId);
    if (exists) {
      get().removeSelectedSuggestedThoughts([thoughtId]);
    } else {
      get().addSelectedSuggestedThought(thoughtId);
    }
  },

  toggleAllSelectedSuggestedThoughts: (allThoughtIds) => {
    const areAllSelected = allThoughtIds.every((id) =>
      get().selectedSuggestedThoughts.includes(id),
    );

    if (areAllSelected) {
      // Remove all suggested thoughts from selection
      get().removeSelectedSuggestedThoughts(allThoughtIds);
      //* Do not unselect situation as suggested sitions render independently
    } else {
      // Add all suggested thoughts to selection
      get().addSelectedSuggestedThoughts(allThoughtIds);
      //* Do not select situation as suggested sitions render independently
    }
  },
  isSelectedSuggestedThought: (thoughtId) => {
    return get().selectedSuggestedThoughts.includes(thoughtId);
  },
  removeSelectedSuggestedThoughts: (thoughtIds) =>
    set((state) => ({
      selectedSuggestedThoughts: state.selectedSuggestedThoughts.filter(
        (id) => !thoughtIds.includes(id),
      ),
    })),

  countSelectedSuggestedThoughts: () => get().selectedSuggestedThoughts.length,
  resetSelectedSuggestedThoughts: () =>
    set({
      selectedSuggestedThoughts: [],
    }),

  //////* Selected Origins  */
  selectedOrigins: [],
  addSelectedOrigin: (originId) => {
    const exists = get().isSelectedOrigin(originId);
    if (exists) return; // do not add duplicates
    set((state) => ({
      selectedOrigins: [...state.selectedOrigins, originId],
    }));
  },

  toggleSelectedOrigin: (originId) => {
    const exists = get().isSelectedOrigin(originId);
    if (exists) {
      get().removeSelectedOrigins([originId]);
    } else {
      get().addSelectedOrigin(originId);
    }
  },

  isSelectedOrigin: (originId) => {
    return get().selectedOrigins.includes(originId);
  },

  removeSelectedOrigins: (originIds) =>
    set((state) => ({
      selectedOrigins: state.selectedOrigins.filter(
        (id) => !originIds.includes(id),
      ),
    })),

  countSelectedOrigins: () => get().selectedOrigins.length,

  getSelectedOriginIds: () => {
    return get().selectedOrigins;
  },

  resetSelectedOrigins: () =>
    set({
      selectedOrigins: [],
    }),
});

export function pickPersistedUserChoicesState(
  state: PersistableUserChoicesState,
) {
  return {
    selectedSituations: state.selectedSituations,
    selectedThoughts: state.selectedThoughts,
    selectedThoughtsFree: state.selectedThoughtsFree,
    searchQueryCheck: state.searchQueryCheck,
    searchQuerySelect: state.searchQuerySelect,
    searchQueryBacklog: state.searchQueryBacklog,
    checkFilters: state.checkFilters,
    checkSemanticRatio: state.checkSemanticRatio,
    checkHitsPerPage: state.checkHitsPerPage,
    checkSortBy: state.checkSortBy,
    selectFilters: state.selectFilters,
    selectSemanticRatio: state.selectSemanticRatio,
    selectHitsPerPage: state.selectHitsPerPage,
    selectSortBy: state.selectSortBy,
    backlogFilters: state.backlogFilters,
    backlogSemanticRatio: state.backlogSemanticRatio,
    backlogHitsPerPage: state.backlogHitsPerPage,
    backlogSortBy: state.backlogSortBy,
    targetsToRerank: state.targetsToRerank,
    activeBacklogView: state.activeBacklogView,
    activeStudioGrouping: state.activeStudioGrouping,
    activeStudioContent: state.activeStudioContent,
    activeStudioExportLock: state.activeStudioExportLock,
    activePrototypeCategory: state.activePrototypeCategory,
    activeProfileVersion: state.activeProfileVersion,
    studioSituationLoadState: state.studioSituationLoadState,
  };
}

export function normalizePersistedUserChoicesState(
  state: Partial<PersistableUserChoicesState>,
): Partial<PersistableUserChoicesState> {
  const normalizedThoughts = state.selectedThoughts ?? [];
  const normalizedThoughtsFree =
    state.selectedThoughtsFree && state.selectedThoughtsFree.length > 0
      ? state.selectedThoughtsFree
      : buildSelectedThoughtsFreeFromSelectedThoughts(normalizedThoughts);

  return {
    ...state,
    selectedThoughts: normalizedThoughts,
    selectedThoughtsFree: normalizedThoughtsFree,
    activeProfileVersion:
      state.activeProfileVersion?.id && state.activeProfileVersion?.name
        ? state.activeProfileVersion
        : null,
    studioSituationLoadState: state.studioSituationLoadState ?? {},
  };
}
